import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import {
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  PackageX,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/utils-date";

const ICONS = {
  clock: Clock,
  "package-x": PackageX,
  "alert-triangle": AlertTriangle,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
};

const INSIGHT_TONES = {
  expiry: { bg: "bg-[#FDF6E3]", border: "border-[#F2DEB0]", icon: "text-[#8B6518]", iconBg: "bg-[#FAEFC6]" },
  stagnant: { bg: "bg-[#EDF5F9]", border: "border-[#CFE4F0]", icon: "text-[#2F5A78]", iconBg: "bg-[#DCEAF3]" },
  low_stock: { bg: "bg-[#FCEAE8]", border: "border-[#F5C7BD]", icon: "text-[#8E3B2A]", iconBg: "bg-[#F7D6CC]" },
  trend_up: { bg: "bg-[#E8F3EC]", border: "border-[#C5E1D1]", icon: "text-[#2B5A3F]", iconBg: "bg-[#D3E8DA]" },
  trend_down: { bg: "bg-[#FCEAE8]", border: "border-[#F5C7BD]", icon: "text-[#8E3B2A]", iconBg: "bg-[#F7D6CC]" },
};

function formatValue(value, unit, metric) {
  if (metric === "revenue" || metric === "avg_ticket") {
    return formatCurrency(value || 0);
  }
  return `${Math.round(value ?? 0).toLocaleString("it-IT")}${unit ? ` ${unit}` : ""}`;
}

function formatTickDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

export default function KpiDetailSheet({ open, onOpenChange, metric, days, accentColor = "#2E4A3B" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !metric) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    api
      .kpiDetail(metric, days)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, metric, days]);

  const trend = data?.trend;
  const deltaPct = data?.delta_pct;
  const hasDelta = typeof deltaPct === "number";
  const TrendIcon = trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : null;
  const trendColor =
    trend === "up" ? "text-[#2B5A3F] bg-[#E8F3EC] border-[#C5E1D1]" :
    trend === "down" ? "text-[#8E3B2A] bg-[#FCEAE8] border-[#F5C7BD]" :
    "text-[#5C5F5A] bg-[#F4F1ED] border-[#EAE7E1]";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="kpi-detail-sheet"
        className="w-full sm:max-w-md lg:max-w-lg overflow-y-auto p-0 flex flex-col gap-0"
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#EAE7E1]">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C]">
            In evidenza
          </p>
          <SheetTitle
            className="text-2xl font-semibold text-left"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            {data?.label || (metric ? metric : "Dettaglio")}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Dettaglio della metrica con confronto periodo precedente, andamento nel tempo e suggerimenti.
          </SheetDescription>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 px-6 py-6 space-y-6">
          {loading && (
            <div data-testid="kpi-detail-loading" className="space-y-3">
              <div className="h-10 w-40 rounded-md bg-[#F4F1ED] animate-pulse" />
              <div className="h-6 w-24 rounded-md bg-[#F4F1ED] animate-pulse" />
              <div className="h-56 w-full rounded-lg bg-[#F4F1ED] animate-pulse mt-4" />
            </div>
          )}

          {!loading && data && (
            <>
              {/* Hero value + delta */}
              <div data-testid="kpi-detail-hero">
                <div
                  className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1A1C1A]"
                  style={{ fontFamily: "Work Sans, sans-serif" }}
                >
                  {formatValue(data.current.value, data.unit, data.metric)}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {hasDelta && TrendIcon && (
                    <span
                      data-testid="kpi-detail-delta"
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${trendColor}`}
                    >
                      <TrendIcon size={13} />
                      {deltaPct > 0 ? "+" : ""}
                      {deltaPct}%
                    </span>
                  )}
                  {!hasDelta && (
                    <span
                      data-testid="kpi-detail-delta-new"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-[#EAE7E1] bg-[#F4F1ED] text-[#5C5F5A]"
                    >
                      <Sparkles size={12} />
                      Nuovo periodo
                    </span>
                  )}
                  <span className="text-xs text-[#8E918C]">
                    Rispetto ai {data.current.period_days} giorni precedenti
                  </span>
                </div>
                <div className="mt-2 text-xs text-[#8E918C]">
                  Periodo precedente:{" "}
                  <span className="font-medium text-[#5C5F5A]">
                    {formatValue(data.previous.value, data.unit, data.metric)}
                  </span>
                </div>
              </div>

              {/* Insights ("Come migliorare") */}
              {data.insights?.length > 0 && (
                <section data-testid="kpi-detail-insights">
                  <h3
                    className="text-sm font-semibold text-[#1A1C1A] mb-3"
                    style={{ fontFamily: "Work Sans, sans-serif" }}
                  >
                    Come migliorare
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {data.insights.map((it, idx) => {
                      const Icon = ICONS[it.icon] || Sparkles;
                      const tone = INSIGHT_TONES[it.type] || INSIGHT_TONES.stagnant;
                      const inner = (
                        <div
                          data-testid={`kpi-insight-${it.type}`}
                          className={`flex items-start gap-3 p-4 rounded-xl border ${tone.bg} ${tone.border} ${
                            it.cta_route ? "hover:shadow-sm transition-shadow cursor-pointer" : ""
                          }`}
                        >
                          <div className={`h-9 w-9 rounded-lg ${tone.iconBg} ${tone.icon} flex items-center justify-center flex-shrink-0`}>
                            <Icon size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#1A1C1A]">{it.title}</div>
                            <div className="text-xs text-[#5C5F5A] mt-1 leading-relaxed">{it.body}</div>
                            {it.cta_label && (
                              <div className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${tone.icon}`}>
                                {it.cta_label}
                                <ChevronRight size={12} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                      return it.cta_route ? (
                        <Link key={idx} to={it.cta_route} onClick={() => onOpenChange(false)}>
                          {inner}
                        </Link>
                      ) : (
                        <div key={idx}>{inner}</div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Trend chart */}
              <section data-testid="kpi-detail-chart">
                <div className="flex items-baseline justify-between mb-3">
                  <h3
                    className="text-sm font-semibold text-[#1A1C1A]"
                    style={{ fontFamily: "Work Sans, sans-serif" }}
                  >
                    Andamento nel tempo
                  </h3>
                  <span className="text-[10px] uppercase tracking-wider text-[#8E918C]">
                    Ultimi {data.current.period_days} giorni
                  </span>
                </div>
                <div className="h-60 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.timeseries} margin={{ top: 10, right: 4, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id={`kpi-grad-${metric}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={accentColor} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={accentColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#EAE7E1" vertical={false} />
                      <XAxis
                        dataKey="date"
                        stroke="#8E918C"
                        fontSize={10}
                        tickFormatter={formatTickDate}
                        minTickGap={24}
                      />
                      <YAxis
                        stroke="#8E918C"
                        fontSize={10}
                        tickFormatter={(v) =>
                          data.metric === "revenue" || data.metric === "avg_ticket" ? `€${v}` : v
                        }
                      />
                      <Tooltip
                        contentStyle={{ background: "#FFFFFF", border: "1px solid #EAE7E1", borderRadius: 8, fontSize: 12 }}
                        formatter={(v) => [formatValue(v, data.unit, data.metric), data.label]}
                        labelFormatter={(v) => new Date(v).toLocaleDateString("it-IT")}
                      />
                      {data.previous_avg_per_day > 0 && (
                        <ReferenceLine
                          y={data.previous_avg_per_day}
                          stroke="#8E918C"
                          strokeDasharray="4 4"
                          ifOverflow="extendDomain"
                        />
                      )}
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={accentColor}
                        strokeWidth={2}
                        fill={`url(#kpi-grad-${metric})`}
                        connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-4 mt-3 text-[11px] text-[#5C5F5A] flex-wrap">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-0.5 w-4 rounded-full" style={{ background: accentColor }} />
                    Il tuo negozio
                  </span>
                  {data.previous_avg_per_day > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block h-0 w-4 border-t border-dashed border-[#8E918C]" />
                      Media giorno periodo precedente:{" "}
                      <span className="font-medium text-[#1A1C1A]">
                        {formatValue(data.previous_avg_per_day, data.unit, data.metric)}
                      </span>
                    </span>
                  )}
                </div>
              </section>
            </>
          )}

          {!loading && !data && (
            <div data-testid="kpi-detail-error" className="text-sm text-[#8E918C]">
              Impossibile caricare i dettagli. Riprova fra un attimo.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
